import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';

const ACTIVITY_KINDS = new Set([
  'trade',
  'claim',
  'claim_backer_fees',
  'back',
  'unback',
  'boost',
  'gift_received',
  'gift_sent',
]);
const MARKET_PRESENTATION_FIELDS = new Set([
  'marketTitle',
  'marketCoverImageUrl',
  'marketCoverImageNsfw',
]);
const GIFT_PRESENTATION_FIELDS = new Set([
  'opaqueId',
  'counterpartyHandle',
  'giftStatus',
  'assetKind',
]);

export interface ActivityEntityDiagnostic {
  readonly kind: 'unknown-source';
  readonly entityHash: string;
}

export interface ActivityFieldProjection {
  readonly found: boolean;
  readonly value?: unknown;
}

/** Canonical resources that can enrich one normalized activity row. The
 * market, participation, trade, and outcome stores are intentionally
 * unscoped; gifts are the one network-scoped canonical resource. */
export function activitySourceEntityKeys(
  queryClient: QueryClient,
  row: Readonly<Record<string, unknown>>,
  networkScope: string,
): readonly QueryKey[] {
  const keys: QueryKey[] = [];
  const tradeEventId = nonEmptyString(row.tradeEventId);
  const participationId = nonEmptyString(row.participationId);
  const giftPda = nonEmptyString(row.giftPda);
  const market = nonEmptyString(row.market);

  if (tradeEventId !== undefined) keys.push(['trades', 'entity', tradeEventId]);
  if (participationId !== undefined) keys.push(['participation', 'entity', participationId]);
  if (giftPda !== undefined) keys.push(['gifts', 'entity', networkScope, giftPda]);
  if (market !== undefined) {
    const marketKey = ['markets', 'entity', market] as const;
    keys.push(marketKey);
    const outcomeKey = activityOutcomeEntityKey(queryClient, marketKey, row.outcomeIndex);
    if (outcomeKey !== undefined) keys.push(outcomeKey);
  }

  const kind = nonEmptyString(row.kind);
  if (kind !== undefined && !ACTIVITY_KINDS.has(kind)) {
    emit('activity-entity:diagnostic', {
      kind: 'unknown-source',
      entityHash: hashDiagnosticEntity(nonEmptyString(row.id) ?? kind),
    } satisfies ActivityEntityDiagnostic);
  }
  return keys;
}

/** Canonical resources that can change one projected activity field. Field
 * hooks use this narrower routing so unrelated trade / participation / market
 * entities do not acquire an observer for every event-time activity fact. */
export function activitySourceEntityKeysForField(
  queryClient: QueryClient,
  row: Readonly<Record<string, unknown>>,
  networkScope: string,
  field: string,
): readonly QueryKey[] {
  const market = nonEmptyString(row.market);
  if (market !== undefined && MARKET_PRESENTATION_FIELDS.has(field)) {
    return [['markets', 'entity', market]];
  }
  if (market !== undefined && field === 'outcomeLabel') {
    const marketKey = ['markets', 'entity', market] as const;
    const outcomeKey = activityOutcomeEntityKey(queryClient, marketKey, row.outcomeIndex);
    return outcomeKey === undefined ? [marketKey] : [marketKey, outcomeKey];
  }
  const giftPda = nonEmptyString(row.giftPda);
  if (giftPda !== undefined && GIFT_PRESENTATION_FIELDS.has(field)) {
    return [['gifts', 'entity', networkScope, giftPda]];
  }
  return [];
}

/** Overlay mutable presentation from canonical sources while event-time facts
 * and REST fallbacks remain owned by the activity entity. */
export function materializeActivityEntityRow<Row>(
  queryClient: QueryClient,
  row: Row,
  networkScope: string,
): Row {
  if (row === null || typeof row !== 'object') return row;
  const record = row as Record<string, unknown>;
  let materialized = record;
  for (const key of activitySourceEntityKeys(queryClient, record, networkScope)) {
    const source = queryClient.getQueryData<Record<string, unknown>>(key);
    if (source === undefined) continue;
    const projected = activityPresentationFromSource(key, record, source);
    if (Object.keys(projected).length > 0) materialized = { ...materialized, ...projected };
  }
  return materialized as Row;
}

/** Resolve one activity presentation field from the source observer results.
 * `found` distinguishes a legitimate `null` from "this source does not own
 * the field", allowing the activity REST value to remain the fallback. */
export function projectActivityEntityField(
  row: Readonly<Record<string, unknown>>,
  field: string,
  sourceKeys: readonly QueryKey[],
  sources: readonly (Readonly<Record<string, unknown>> | undefined)[],
): ActivityFieldProjection {
  for (let index = sourceKeys.length - 1; index >= 0; index -= 1) {
    const key = sourceKeys[index];
    const source = sources[index];
    if (key === undefined || source === undefined) continue;
    const projected = activityPresentationFromSource(key, row, source);
    if (field in projected) return { found: true, value: projected[field] };
  }
  return { found: false };
}

function activityOutcomeEntityKey(
  queryClient: QueryClient,
  marketKey: QueryKey,
  rawOutcomeIndex: unknown,
): QueryKey | undefined {
  if (typeof rawOutcomeIndex !== 'number' || !Number.isInteger(rawOutcomeIndex)) return undefined;
  const market = queryClient.getQueryData<Record<string, unknown>>(marketKey);
  const outcomes = market?.outcomes;
  if (!Array.isArray(outcomes)) return undefined;
  for (const value of outcomes) {
    if (value === null || typeof value !== 'object') continue;
    const outcome = value as Record<string, unknown>;
    if (outcome.outcomeIndex !== rawOutcomeIndex) continue;
    const mint = nonEmptyString(outcome.mint);
    if (mint !== undefined) return ['outcomes', 'entity', mint];
  }
  return undefined;
}

function activityPresentationFromSource(
  key: QueryKey,
  activity: Readonly<Record<string, unknown>>,
  source: Readonly<Record<string, unknown>>,
): Record<string, unknown> {
  const resource = key[0];
  if (resource === 'markets') {
    return definedFields({
      marketTitle: source.title,
      marketCoverImageUrl: source.coverImageUrl,
      marketCoverImageNsfw: source.coverImageNsfw,
    });
  }
  if (resource === 'outcomes') return definedFields({ outcomeLabel: source.label });
  if (resource === 'gifts') {
    const kind = activity.kind;
    const counterpartyHandle =
      kind === 'gift_received'
        ? source.senderHandle
        : kind === 'gift_sent'
          ? source.recipientHandle
          : undefined;
    return definedFields({
      opaqueId: source.opaqueId,
      counterpartyHandle,
      giftStatus: source.status,
      assetKind: source.assetKind,
    });
  }
  return {};
}

function definedFields(values: Readonly<Record<string, unknown>>): Record<string, unknown> {
  const defined: Record<string, unknown> = {};
  for (const [field, value] of Object.entries(values)) {
    if (value !== undefined) defined[field] = value;
  }
  return defined;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}
